import { Alert, Box, Typography } from '@mui/material';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import {
  ROLE_LABELS,
  createAdminApi,
  roleSatisfies,
  type AdminFailedMessage,
  type AdminPage,
  type AdminRole,
  type SensitiveActionInput,
} from '@/admin/adminApi';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { SensitiveActionDialog } from '@/admin/SensitiveActionDialog';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';
import { adminErrorCode } from '@/admin/settings/adminErrorMessage';
import { AccessPanel } from '@/admin/users/accountsUi';
import { httpStatusOf, messageOf } from '@/admin/users/adminErrors';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { useHttpClient } from '@/app/providers';
import { fontStacks } from '@/ui/tokens';
import { useNavBadges } from '@/ui/shell/navBadges';
import {
  AccessDenied,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  type DataTableColumn,
} from '@/ui/kit';

import {
  absoluteInstant,
  describeTally,
  previewError,
  processingLabel,
  relativeInstant,
  shortClassName,
} from './failedMessagePresentation';
import { useOperationsRefresh } from './useOperationsRefresh';

/**
 * Traitements en erreur : les messages que la file n'a pas pu traiter.
 *
 * Six regles, chacune testee :
 *
 * 1. **Le total est celui du serveur**, jamais le nombre de lignes de la page. La
 *    pastille du menu, lue sur la meme route, dit la meme chose.
 * 2. **Aucun identifiant en valeur principale** : on lit « Invitation de
 *    coparent » et le nom court de la classe ; l'identifiant du message n'apparait
 *    que dans la confirmation, ou le serveur l'exige.
 * 3. **Rejouer = remettre en file.** Rien n'est execute pendant l'action : le
 *    worker traite ensuite, et un echec nouveau ramene le message ici. L'ecran
 *    l'ecrit, il ne dit pas « corrigé ».
 * 4. **« Rejouer la sélection » est une boucle du client**, un appel par message
 *    (une ligne d'audit chacun, la confirmation serveur restant l'identifiant du
 *    message) : aucune route de rejeu groupe n'existe. Elle **s'arrete au premier
 *    echec** — la cause est probablement commune —, et le bilan dit ce qui est
 *    parti, ce qui a echoue et ce qui n'a pas ete tente. La saisie humaine du
 *    dialogue porte sur le **nombre** de messages.
 * 5. **Apres un rejeu, la liste est relue au serveur**, ainsi que la pastille du
 *    menu : aucune ligne n'est retiree a la main.
 * 6. **`support` lit, ne rejoue pas** : le bouton est absent, et l'ecran dit
 *    pourquoi. Le serveur reste l'autorite (403).
 */

const PER_PAGE = 25;

/** Entree du registre de navigation dont la pastille change avec cet ecran. */
const BADGE_ENTRY_ID = 'jobs';

const retryPolicy = SENSITIVE_ACTIONS.retry_failed_message;

const intParam = (value: string | null): number => {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

const describeRetryFailure = (error: unknown): string => {
  switch (adminErrorCode(error) ?? (httpStatusOf(error) === 404 ? 'not_found' : null)) {
    case 'not_found':
      return 'Ce message n’est plus dans le transport d’échec : il a déjà été rejoué. La liste est relue.';
    case 'confirmation_mismatch':
      return 'La saisie ne correspond pas au message visé. Rien n’a été rejoué.';
    case 'forbidden':
      return 'Votre rôle d’administration ne permet pas de rejouer un traitement. Rien n’a été rejoué.';
    default:
      return messageOf(error);
  }
};

type Dialog =
  | { readonly kind: 'one'; readonly message: AdminFailedMessage }
  | { readonly kind: 'many'; readonly messages: readonly AdminFailedMessage[] };

type Notice =
  | { readonly kind: 'one'; readonly label: string }
  | { readonly kind: 'tally'; readonly text: string; readonly failure: string | null };

const ErrorCell = ({
  error,
  expanded,
  onToggle,
}: {
  error: string;
  expanded: boolean;
  onToggle: () => void;
}): ReactElement => {
  const { text, expandable } = previewError(error, expanded);

  return (
    <Box>
      <Typography
        component="div"
        sx={{
          fontFamily: fontStacks.mono,
          fontSize: 13,
          color: 'tokens.criticalText',
          overflowWrap: 'anywhere',
          whiteSpace: expanded ? 'pre-wrap' : 'normal',
        }}
      >
        {text}
      </Typography>
      {expandable ? (
        <Button variant="ghost" size="S" aria-expanded={expanded} onClick={onToggle}>
          {expanded ? 'Réduire' : 'Afficher tout'}
        </Button>
      ) : null}
    </Box>
  );
};

const Pagination = ({
  page,
  total,
  busy,
  onPage,
}: {
  page: AdminPage<AdminFailedMessage>;
  total: number;
  busy: boolean;
  onPage: (next: number) => void;
}): ReactElement => {
  const known = total >= 0;
  const pages = known ? Math.max(1, Math.ceil(total / page.perPage)) : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      <Button
        size="S"
        disabled={page.page <= 1 || busy}
        onClick={() => {
          onPage(page.page - 1);
        }}
      >
        Page précédente
      </Button>
      <Typography role="status" sx={{ fontSize: 13, color: 'tokens.textMuted', textAlign: 'center' }}>
        {known
          ? `${total.toLocaleString('fr-FR')} traitement${total > 1 ? 's' : ''} en erreur · page ${String(page.page)} sur ${String(pages)}`
          : 'Nombre total non communiqué par le serveur.'}
      </Typography>
      <Button
        size="S"
        disabled={(pages === null ? page.items.length < page.perPage : page.page >= pages) || busy}
        onClick={() => {
          onPage(page.page + 1);
        }}
      >
        Page suivante
      </Button>
    </Box>
  );
};

const FailedMessages = ({
  role,
  operatorEmail,
  token,
  refresh,
}: {
  role: AdminRole;
  operatorEmail: string;
  token: number;
  refresh: () => void;
}): ReactElement => {
  const http = useHttpClient();
  const api = useMemo(() => createAdminApi(http), [http]);
  const badges = useNavBadges();
  const [params, setParams] = useSearchParams();
  const pageNumber = intParam(params.get('page'));
  const canRetry = roleSatisfies(role, 'admin');

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [pending, setPending] = useState(false);
  const [dialogError, setDialogError] = useState<{ readonly message: string; readonly code: string | null } | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  // L'instant de lecture accompagne la page : les dates relatives se disent
  // « il y a 3 h » depuis la lecture, et ne changent pas a chaque rendu.
  const list = useAdminQuery(
    `failed-messages|${String(pageNumber)}`,
    async () => ({ page: await api.listFailedMessages(pageNumber, PER_PAGE), readAt: Date.now() }),
    { token },
  );
  const page = list.data?.page ?? null;
  const now = list.data?.readAt ?? 0;
  const failure = list.failure;
  const rows = useMemo(() => page?.items ?? [], [page]);
  // Derivee de la page affichee : une selection ne survit pas a une ligne disparue.
  const selectedRows = rows.filter((row) => selected.has(row.id));
  const afterReplay = useCallback(
    (replayedIds: readonly string[]): void => {
      setSelected((previous) => new Set([...previous].filter((id) => !replayedIds.includes(id))));
      refresh();
      badges.refresh(BADGE_ENTRY_ID);
    },
    [badges, refresh],
  );

  const toggle = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(set);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    return next;
  };

  const goToPage = (next: number): void => {
    const nextParams = new URLSearchParams(params);

    if (next <= 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(next));
    }

    setSelected(new Set());
    setParams(nextParams);
  };

  const openDialog = (next: Dialog): void => {
    setDialogError(null);
    setNotice(null);
    setDialog(next);
  };

  const closeDialog = (): void => {
    if (!pending) {
      setDialog(null);
      setDialogError(null);
    }
  };

  const retryOne = async (message: AdminFailedMessage, input: SensitiveActionInput): Promise<void> => {
    setPending(true);
    setDialogError(null);

    try {
      await api.retryFailedMessage(message.id, input);
      setDialog(null);
      setNotice({ kind: 'one', label: processingLabel(message.class) });
      afterReplay([message.id]);
    } catch (error: unknown) {
      setDialogError({ message: describeRetryFailure(error), code: adminErrorCode(error) });

      // Deja rejoue ailleurs : la liste affichee est en retard, on la relit.
      if (httpStatusOf(error) === 404) {
        refresh();
      }
    } finally {
      setPending(false);
    }
  };

  const retryMany = async (messages: readonly AdminFailedMessage[], input: SensitiveActionInput): Promise<void> => {
    setPending(true);
    setDialogError(null);

    const replayed: string[] = [];
    let failure: string | null = null;

    for (const message of messages) {
      try {
        // Un appel par message : une ligne d'audit chacun. La confirmation du
        // serveur est l'identifiant du message, ce que l'operateur a retape
        // etant le nombre — qui n'est pas envoye.
        await api.retryFailedMessage(message.id, {
          reasonCode: input.reasonCode,
          ...(input.reason === undefined ? {} : { reason: input.reason }),
          confirmation: message.id,
        });
        replayed.push(message.id);
      } catch (error: unknown) {
        const sentAt = relativeInstant(message.failedAt, Date.now());

        failure = `${processingLabel(message.class)}${sentAt === null ? '' : ` (échoué ${sentAt})`} : ${describeRetryFailure(error)}`;
        break;
      }
    }

    const failed = failure === null ? 0 : 1;

    setDialog(null);
    setNotice({
      kind: 'tally',
      text: describeTally({ replayed: replayed.length, failed, notTried: messages.length - replayed.length - failed }),
      failure,
    });
    setPending(false);
    // Les messages deja partis ne reviennent pas : la liste et la pastille se relisent, echec ou non.
    afterReplay(replayed);
  };

  const confirm = (input: SensitiveActionInput): void => {
    if (dialog === null) {
      return;
    }

    void (dialog.kind === 'one' ? retryOne(dialog.message, input) : retryMany(dialog.messages, input));
  };

  const columns: readonly DataTableColumn<AdminFailedMessage>[] = [
    ...(canRetry
      ? [
          {
            key: 'select',
            header: 'Sélection',
            render: (message: AdminFailedMessage): ReactElement => (
              <Checkbox
                label={`Sélectionner : ${processingLabel(message.class)}`}
                hideLabel
                checked={selected.has(message.id)}
                onChange={() => {
                  setSelected((previous) => toggle(previous, message.id));
                }}
              />
            ),
          },
        ]
      : []),
    {
      key: 'processing',
      header: 'Traitement',
      primary: true,
      render: (message) => (
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{processingLabel(message.class)}</Typography>
          <Typography sx={{ fontSize: 11, color: 'tokens.textMuted', fontFamily: fontStacks.mono, whiteSpace: 'nowrap' }}>
            {shortClassName(message.class)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'error',
      header: 'Erreur',
      // Absente, l'erreur est « — » (le tableau l'ecrit pour `null`) : un element
      // qui rendrait `null` laisserait une case blanche.
      render: (message) =>
        message.error === null || message.error === '' ? null : (
          <ErrorCell
            error={message.error}
            expanded={expanded.has(message.id)}
            onToggle={() => {
              setExpanded((previous) => toggle(previous, message.id));
            }}
          />
        ),
    },
    {
      key: 'failedAt',
      header: 'Échec',
      render: (message) => {
        const relative = relativeInstant(message.failedAt, now);

        return relative === null ? null : (
          <time
            dateTime={message.failedAt ?? undefined}
            title={absoluteInstant(message.failedAt) ?? undefined}
            style={{ whiteSpace: 'nowrap' }}
          >
            {relative}
          </time>
        );
      },
    },
    ...(canRetry
      ? [
          {
            key: 'action',
            header: 'Action',
            align: 'right' as const,
            render: (message: AdminFailedMessage): ReactElement => (
              <Button
                size="S"
                icon={RotateCcw}
                aria-label={`Rejouer : ${processingLabel(message.class)}${message.failedAt === null ? '' : `, échoué ${relativeInstant(message.failedAt, now) ?? ''}`}`}
                onClick={() => {
                  openDialog({ kind: 'one', message });
                }}
              >
                Rejouer
              </Button>
            ),
          },
        ]
      : []),
  ];

  const total = page?.total ?? -1;
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const subtitleFor = (message: AdminFailedMessage): string => {
    const relative = relativeInstant(message.failedAt, now);

    return `${processingLabel(message.class)}${relative === null ? '' : ` · échoué ${relative}`}`;
  };

  return (
    <>
      {canRetry ? null : (
        <Alert severity="info" variant="outlined" role="note">
          Votre rôle ({ROLE_LABELS[role]}) permet de consulter ces traitements, pas de les rejouer : le rejeu est
          réservé au rôle Administrateur.
        </Alert>
      )}

      {notice?.kind === 'one' ? (
        <Alert severity="success" variant="outlined" role="status" onClose={() => { setNotice(null); }}>
          « {notice.label} » est remis en file : le worker va le traiter. S’il échoue de nouveau, il reviendra dans cette liste.
        </Alert>
      ) : null}
      {notice?.kind === 'tally' ? (
        <Alert
          severity={notice.failure === null ? 'success' : 'warning'}
          variant="outlined"
          role={notice.failure === null ? 'status' : 'alert'}
          onClose={() => { setNotice(null); }}
        >
          <strong>{notice.text}</strong>
          {notice.failure === null ? null : <Box sx={{ mt: 0.5 }}>Échec : {notice.failure}</Box>}
        </Alert>
      ) : null}

      <Card
        title="Messages que la file n’a pas pu traiter"
        subtitle="Rejouer remet un message en file : le worker le traite ensuite, rien n’est exécuté pendant l’action."
        actions={
          canRetry && rows.length > 0 ? (
            <>
              <Checkbox
                label="Tout sélectionner"
                checked={allSelected}
                onChange={(checked) => {
                  setSelected(checked ? new Set(rows.map((row) => row.id)) : new Set());
                }}
              />
              <Button
                variant="primary"
                size="S"
                icon={RotateCcw}
                disabled={selectedRows.length === 0}
                onClick={() => {
                  openDialog({ kind: 'many', messages: selectedRows });
                }}
              >
                {`Rejouer la sélection (${String(selectedRows.length)})`}
              </Button>
            </>
          ) : undefined
        }
      >
        {failure?.forbidden === true ? (
          <AccessDenied title="Votre rôle ne permet pas de consulter les traitements en erreur">
            Votre compte reste connecté : il manque le rôle d’administration requis (au moins « Support »).
          </AccessDenied>
        ) : null}
        {failure !== null && !failure.forbidden ? <ErrorState message={failure.message} onRetry={refresh} /> : null}
        {page === null && failure === null ? <LoadingState label="Chargement des traitements en erreur…" /> : null}
        {page !== null && rows.length === 0 && pageNumber > 1 && total > 0 ? (
          <EmptyState
            title="Cette page est vide"
            description="Des traitements ont été rejoués depuis la dernière lecture : il n’y a plus de page à cet endroit."
            action={
              <Button
                onClick={() => {
                  goToPage(1);
                }}
              >
                Première page
              </Button>
            }
          />
        ) : null}
        {page !== null && rows.length === 0 && !(pageNumber > 1 && total > 0) ? (
          <EmptyState
            title="Aucun traitement en erreur"
            description="La file n’a rien rangé dans le transport d’échec. Ce n’est pas une erreur : c’est l’état attendu."
          />
        ) : null}
        {page !== null && rows.length > 0 ? (
          <>
            <DataTable<AdminFailedMessage>
              label="Traitements en erreur"
              columns={columns}
              rows={rows}
              getRowId={(message) => message.id}
            />
            <Pagination page={page} total={total} busy={list.busy} onPage={goToPage} />
          </>
        ) : null}
      </Card>

      {dialog === null ? null : (
        <SensitiveActionDialog
          open
          title={dialog.kind === 'one' ? 'Rejouer ce traitement' : `Rejouer ${String(dialog.messages.length)} traitements`}
          subtitle={dialog.kind === 'one' ? subtitleFor(dialog.message) : 'Un appel par message, dans l’ordre de la liste, arrêté au premier échec.'}
          consequence={{
            disappears: [],
            kept: [
              'Le message est remis en file : le worker le traite ensuite.',
              'Il quitte la liste des traitements en erreur.',
              'S’il échoue de nouveau, il revient ici.',
            ],
            keptTitle: 'Ce que fait le rejeu',
          }}
          reasonOptions={retryPolicy.reasons}
          requiresPassword={retryPolicy.requiresPassword}
          operatorEmail={operatorEmail}
          expectedConfirmation={dialog.kind === 'one' ? dialog.message.id : String(dialog.messages.length)}
          confirmationLabel={dialog.kind === 'one' ? 'l’identifiant du message' : 'le nombre de messages'}
          actionLabel={dialog.kind === 'one' ? 'Rejouer ce traitement' : `Rejouer ${String(dialog.messages.length)} traitements`}
          pending={pending}
          error={dialogError?.message ?? null}
          errorCode={dialogError?.code ?? null}
          onCancel={closeDialog}
          onConfirm={confirm}
        />
      )}
    </>
  );
};

export const FailedMessagesScreen = (): ReactElement => {
  const { access, reload } = useAdminAccess();
  const { token, refresh } = useOperationsRefresh();
  const granted = access.kind === 'granted';

  return (
    <Box sx={{ display: 'grid', gap: 2.5, mt: 0.5, mb: 4 }}>
      <PageHeader
        eyebrow="Exploitation"
        title="Traitements en erreur"
        subtitle="Les messages que la file n’a pas pu traiter après plusieurs tentatives. Chaque lecture est inscrite au journal d’audit : la liste ne se rafraîchit pas seule."
        actions={
          granted ? (
            <Button icon={RefreshCw} onClick={refresh}>
              Actualiser
            </Button>
          ) : undefined
        }
      />
      <AccessPanel access={access} onRetry={reload} />
      {access.kind === 'granted' ? (
        <FailedMessages role={access.role} operatorEmail={access.identity.email} token={token} refresh={refresh} />
      ) : null}
    </Box>
  );
};
